import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../services/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { getModulosAcessiveis } from '../services/api';

interface AuthContextType {
    session: Session | null;
    user: User | null;
    role: string | null;
    loading: boolean;
    signOut: () => void;
    modulosAcessiveis: string[] | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [modulosAcessiveis, setModulosAcessiveis] = useState<string[] | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const sessionRef = useRef<Session | null>(null);

    useEffect(() => {
        const handleAuthStateChange = async (session: Session | null) => {
            // Se o token for o mesmo, não faz nada
            if (session?.user?.id === sessionRef.current?.user?.id) {
                setLoading(false); // Garante que o loading termine se a sessão não mudou
                return;
            }

            sessionRef.current = session;
            setLoading(true);
            setSession(session);
            const currentUser = session?.user ?? null;
            setUser(currentUser);
            let userRole = currentUser?.app_metadata?.role || null;
            if (userRole === 'user') {
                userRole = 'Usuário Padrão';
            }
            setRole(userRole);

            if (userRole) {
                try {
                    const modulos = await getModulosAcessiveis(userRole);
                    setModulosAcessiveis(modulos);
                    setLoading(false); // Move here
                } catch (error) {
                    console.error("Erro ao buscar módulos acessíveis:", error);
                    setModulosAcessiveis(null);
                    setLoading(false); // Move here
                }
            } else {
                setModulosAcessiveis(null);
                setLoading(false); // Move here
            }
        };

        supabase.auth.getSession().then(({ data: { session } }) => {
            handleAuthStateChange(session);
        });

        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            handleAuthStateChange(session);
        });

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, []);

    const signOut = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const value = {
        session,
        user,
        role,
        loading,
        signOut,
        modulosAcessiveis,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};